import {
  Autocomplete,
  Box,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import { useEffect, useState } from "react";
import { Plus, RotateCcw, Trash } from "react-feather";
import * as yup from "yup";
import { Employee, ServiceSector, Tier } from "../../models";
import { useData } from "../../utils/useData";

type PlanDetailsProps = {
  form: any;
  serviceSectors: ServiceSector[];
  employees: Employee[];
  editRenewalPeriod: boolean;
};

const AmountAndCurrencySchema = yup.object().shape({
  amount: yup
    .number()
    .positive()
    .moreThan(0, "Tier Amount must be greater than 0")
    .required()
    .typeError("Tier Amount must be a number")
  // Add other fields like currency if necessary
});

const TierSchema = yup.object().shape({
  id: yup.string().notRequired(), // `undefined` is allowed so `notRequired` is used
  name: yup
    .string()
    .required("Tier Name is required")
    .notOneOf([""], "Tier Name cannot be empty"),
  amount: AmountAndCurrencySchema // Use the AmountAndCurrency schema here
});

export const PlanSchema = yup.object().shape({
  name: yup.string().max(100).required(),
  description: yup.string(),
  tiers: yup
    .array()
    .of(TierSchema)
    .min(1, "At least one Tier is required")
    .required(),
  serviceSectorIds: yup.array().of(yup.string()).required()
});

export const PlanDetailsForm = ({
  form,
  serviceSectors,
  employees,
  editRenewalPeriod
}: PlanDetailsProps) => {
  const theme = useTheme();
  const { membership } = useData();

  const [isOpenServiceSectorSelected, setIsOpenServiceSectorSelected] =
    useState(false);

  const [tierErrors, setTierErrors] = useState<
    {
      name: string;
      amount: { amount: string };
    }[]
  >([]);

  const handleAddTier = () => {
    const newTiers = [...form.values.tiers];
    newTiers.push({
      id: undefined,
      name: "",
      amount: {
        amount: 0
      }
    });
    form.setFieldValue("tiers", newTiers);
  };

  const handleRemoveTier = (indexToRemove: number) => {
    // Create a new array excluding the tier at the specified index
    const newTiers = form.values.tiers.filter(
      (_: Tier, index: number) => index !== indexToRemove
    );

    // Update the form state with the new tiers array
    form.setFieldValue("tiers", newTiers);
  };

  const handleTierNameChange = (index: number, newName: string) => {
    const newTiers = [...form.values.tiers];
    newTiers[index].name = newName;
    form.setFieldValue("tiers", newTiers);
  };

  const handleTierAmountChange = (index: number, newAmount: number) => {
    const newTiers = [...form.values.tiers];
    newTiers[index].amount.amount = newAmount;
    form.setFieldValue("tiers", newTiers);
  };

  const renderTier = (tier: Tier, index: number) => {
    const tierNameError = tierErrors?.[index]?.name;
    const tierAmountError = tierErrors?.[index]?.amount?.amount;

    const canDelete =
      tier.id === undefined ||
      !employees.some((e) => e.accounts.some((a) => a.tierId === tier.id));
    const tooltipTitle = !canDelete
      ? "Unable to remove the tier as it currently has employees assigned to it."
      : "Remove Tier";

    return (
      <ListItem
        key={index}
        secondaryAction={
          <Tooltip title={tooltipTitle}>
            <div>
              <IconButton
                disabled={!canDelete}
                edge="end"
                aria-label="delete"
                onClick={() => handleRemoveTier(index)}
              >
                <Trash />
              </IconButton>
            </div>
          </Tooltip>
        }
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={9}>
            <TextField
              variant="standard"
              fullWidth={true}
              value={tier.name}
              onChange={(e) => handleTierNameChange(index, e.target.value)}
              error={!!tierNameError}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              type="number"
              variant="standard"
              fullWidth={true}
              value={tier.amount.amount}
              onChange={(e) =>
                handleTierAmountChange(index, parseFloat(e.target.value))
              }
              error={!!tierAmountError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item>
            <Typography color="error">
              {tierNameError?.replace(`tiers[${index}].name`, "Tier Name")}
            </Typography>
            <Typography color="error">
              {tierAmountError?.replace(
                `tiers[${index}].amount.amount`,
                "Tier Amount"
              )}
            </Typography>
          </Grid>
        </Grid>
      </ListItem>
    );
  };

  // Handle membership tier limit restrictions
  useEffect(() => {
    const newTierErrors = form.values.tiers.map((tier: any, i: number) => {
      if (form.errors.tiers?.[i]) {
        return form.errors.tiers[i];
      }

      if (
        membership.data?.features?.maxTierAmount &&
        form.values.tiers[i].amount.amount >
          membership.data?.features.maxTierAmount
      ) {
        form.setFieldError(
          `tiers[${i}].amount.amount`,
          "Tier amount exceeds the maximum amount"
        );
        form.setFieldTouched(`tiers[${i}].amount.amount`, true);

        return {
          amount: {
            amount: `Tier amount exceeds max amount ${membership.data.features.maxTierAmount}`
          }
        };
      } else {
        form.setFieldError(`tiers[${i}].amount.amount`, undefined);
        return null;
      }
    });

    setTierErrors(newTierErrors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.values.tiers,
    form.errors.tiers,
    membership.data?.features?.maxTierAmount
  ]);

  const getOpenServiceSectorId = (serviceSectorIds: string[]) => {
    for (const serviceSectorId of serviceSectorIds) {
      const found = serviceSectors.find((ss) => ss.id === serviceSectorId);
      if (
        !found?.restrictions.mccCodes.allow.length &&
        !found?.restrictions.mccCodes.block.length &&
        !found?.restrictions.providerNames.allow.length &&
        !found?.restrictions.providerNames.block.length
      ) {
        return serviceSectorId;
      }
    }

    return null;
  };

  useEffect(() => {
    const openServiceSectorId = getOpenServiceSectorId(
      form.values.serviceSectorIds
    );
    if (openServiceSectorId) return setIsOpenServiceSectorSelected(true);
    setIsOpenServiceSectorSelected(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.serviceSectorIds]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={12}>
        <Typography sx={{ mb: 2 }} variant="subtitle1">
          Plan Name
        </Typography>
        <TextField
          value={form.values.name}
          onChange={form.handleChange}
          name="name"
          required
          fullWidth
          size="small"
          error={form.touched.name && Boolean(form.errors.name)}
          helperText={form.touched.name && form.errors.name}
        />
      </Grid>
      <Grid item xs={12} md={12} mt={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography sx={{ mb: 2 }} variant="subtitle1">
              Contribution tiers
            </Typography>
          </Box>
          <Box>
            <IconButton onClick={handleAddTier}>
              <Plus />
            </IconButton>
          </Box>
        </Box>
        <List>{form.values.tiers.map(renderTier)}</List>
        {/* Display an error message if there are no tiers */}
        {form.touched.tiers &&
          form.errors.tiers &&
          form.values.tiers.length === 0 && (
            <Typography color="error" sx={{ mt: 2 }}>
              At least one Tier is required
            </Typography>
          )}
        <Typography sx={{ mt: 2, mb: 4 }} variant="body2">
          Changes to tiers will be effective on the next renewal date
        </Typography>
      </Grid>
      <Grid item xs={12} p={2}>
        <Typography variant="subtitle1">Categories</Typography>
        <Autocomplete
          disabled={isOpenServiceSectorSelected}
          multiple
          options={serviceSectors}
          getOptionLabel={(option) => option.name}
          value={serviceSectors.filter((sector) =>
            form.values.serviceSectorIds?.includes(sector.id)
          )}
          onChange={(_, newValue) => {
            let newIds = newValue.map((sector) => sector.id);
            const openServiceSectorId = getOpenServiceSectorId(newIds);
            if (openServiceSectorId) {
              newIds = [openServiceSectorId];
            }
            form.setFieldValue("serviceSectorIds", newIds);
          }}
          filterSelectedOptions
          renderInput={(params) => (
            <TextField {...params} placeholder="Categories" />
          )}
        />
        {/* Display an error message if there are no tiers */}
        {form.errors.serviceSectorIds && (
          <Typography color="error" sx={{ mt: 2 }}>
            {form.errors.serviceSectorIds}
          </Typography>
        )}
        {isOpenServiceSectorSelected && (
          <Box display="flex" alignItems="center" gap={2}>
            <Typography color="error" sx={{ mt: 2 }} variant="body2">
              You have selected an 'Open' service sector, this will allow the
              HealthNow card to be used anywhere.
            </Typography>
            <RotateCcw
              onClick={() => {
                form.setFieldValue("serviceSectorIds", []);
              }}
              cursor="pointer"
              color={theme.palette.error.light}
              style={{ marginLeft: "auto" }}
              size={30}
            />
          </Box>
        )}
        <Typography sx={{ mt: 2, mb: 4 }} variant="body2">
          Changes to categories will be effective immediately
        </Typography>
        <Typography variant="subtitle1">Renewal Period</Typography>
        <Select
          disabled={!editRenewalPeriod}
          value={form.values.periodInMonths}
          onChange={(e) => {
            form.setFieldValue("periodInMonths", e.target.value);
          }}
          size="small"
        >
          <MenuItem value={1}>1 Month (Renews Monthly)</MenuItem>
          <MenuItem value={6}>6 Months (Renews Bi-Annually)</MenuItem>
          <MenuItem value={12}>12 Months (Renews Annually)</MenuItem>
        </Select>
        {editRenewalPeriod && (
          <Typography sx={{ mt: 2, mb: 4 }} variant="body2">
            Renewal Period cannot be changed after a plan has been created
          </Typography>
        )}
      </Grid>
    </Grid>
  );
};
